import mixpanel from 'mixpanel-browser'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Group, Image, Rect, Text } from 'react-konva'
import useImage from 'use-image'
import iconOpenWeblink from '../../assets/icons/action-newTab_blue.svg'
import iconImageDefault from '../../assets/icons/file-image_darkGray1.svg'
import iconImageHover from '../../assets/icons/file-image_darkGray2.svg'
import iconImageSelected from '../../assets/icons/file-image_darkGray3.svg'
import iconWeblinkDefault from '../../assets/icons/file-web_darkGray1.svg'
import iconWeblinkHover from '../../assets/icons/file-web_darkGray2.svg'
import iconWeblinkSelected from '../../assets/icons/file-web_darkGray3.svg'
import iconUnlinkedReadOnly from '../../assets/icons/link-broken_darkGray1.svg'
import iconUnlinked from '../../assets/icons/link-broken_red.svg'
import iconLinked from '../../assets/icons/link-linked_blue.svg'
import iconLinkedReadOnly from '../../assets/icons/link-linked_darkGray1.svg'
import iconPSD from '../../assets/icons/psd_color.svg'
import { darkGray1, darkGray2, darkGray3, lightGray1 } from '../../constants/colors'
import { FirestoreContext } from '../../firebase'
import { isWeblink } from '../../logic/previews'
import { createTransformMedia } from '../../logic/transforms'
import { KonvaElement, KonvaMouseEvt } from '../../types/konva'
import { MossMediaSelectionTuple, RightClickTarget } from '../../types/moss'
import { IPreview, IPreviewInstance } from '../../types/previews'
// @TODO Reinstate fades when we get rid of lag
// import { fadeFill, fadeInOpacity, fadeOutOpacity } from '../../logic/animations'
import Draggable from './Draggable'
import Transformer from './Transformer'

interface Props {
  src?: string
  previewSrc?: string
  updatedAt: any
  name?: string
  selected: boolean
  readOnly?: boolean
  // note isHoldingSpacebarRef is conditional here because it is intentionally undefined for container previews
  isHoldingSpacebarRef?: React.RefObject<boolean>
  canvasId: string
  inContainer?: boolean
  X: number
  Y: number
  isLinked?: boolean
  newMediaIdsRef?: React.RefObject<{ [key: string]: true }>
  isMultiSelect?: boolean
  loadLargeImage?: boolean
  selectedObjectsRef: React.RefObject<MossMediaSelectionTuple[]>
  dragEndObjectsRef: React.MutableRefObject<any[]>

  instance: IPreviewInstance | null
  preview: IPreview

  // Functions
  onMouseDown?: ((e: KonvaMouseEvt) => void) | null
  initContainerPreviewsDrag?: (e: any, preview: IPreview) => any
  onOpenObject?: (e: any, preview: IPreview, arg2: boolean) => any
  mediaAddToSelected?: (e: KonvaMouseEvt, mediaTuple: MossMediaSelectionTuple, xOr?: false) => void
  addToInstanceRefs?: (instanceId: string, ref: React.MutableRefObject<any>) => void
  onRightClick?: (e: any, target: RightClickTarget) => any

  // Style props
  width: number
  height: number
  scale: number
  zoom?: number
  iconOuterWidth: number
  iconWidth: number
  paddingTop: number
  minTextWidth: number
  iconPadding: number
  fontSize: number
}

// Note this implements both image previews as well as weblinks since they are super similar
export const URLImage: React.FC<Props> = ({
  src,
  previewSrc,
  updatedAt,
  name = '',
  width,
  height,
  scale,
  zoom = 1,
  iconOuterWidth,
  selected,
  readOnly,
  isHoldingSpacebarRef,
  X,
  Y,
  instance,
  preview,
  canvasId,
  inContainer,
  iconWidth,
  onMouseDown,
  initContainerPreviewsDrag,
  onOpenObject,
  paddingTop,
  minTextWidth,
  iconPadding,
  fontSize,
  isLinked,
  mediaAddToSelected,
  newMediaIdsRef,
  isMultiSelect,
  addToInstanceRefs,
  loadLargeImage,
  selectedObjectsRef,
  dragEndObjectsRef,
  onRightClick,
}) => {
  const firestore = useContext(FirestoreContext)
  const imgRef = useRef<KonvaElement>()
  const draggableRef = useRef<KonvaElement>()
  const [isHovered, setIsHovered] = useState<boolean>(false)
  const [isLinkHovered, setIsLinkHovered] = useState<boolean>(false)
  const [isTransforming, setIsTransforming] = useState<boolean>(false)
  const [largeImageLoaded, setLargeImageLoaded] = useState<boolean>(false)

  useEffect(() => {
    // when first created, add to the global object refs lookup hash so we can retrieve the ref based on instanceID
    if (instance && addToInstanceRefs) addToInstanceRefs(instance.instanceId, draggableRef)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (src && loadLargeImage) {
      // kick off a background load of the large version of the image (we use small version as placeholder)
      // note we use document.createElement('img') instead of new Image because of collision with Konva Image
      const img = document.createElement('img')
      img.addEventListener('load', () => setLargeImageLoaded(true), false)
      img.src = `${src}?${updatedAt.toMillis()}`
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, loadLargeImage])

  let isPhotoShop
  if (isWeblink(preview)) {
    isPhotoShop = false
  } else {
    const ext = name.substr(name.lastIndexOf('.') + 1).toLowerCase()
    isPhotoShop = ext === 'psd' || ext === 'psb'
  }

  /*
    cache bust with the updatedAt time UNLESS src is a preview blob

    @TODO For some reason image becomes undefined during the renders when
    transitioning to src from previewSrc for the first time
    this causes a flash of grey loading rectangle and on slow connections
    seems to disrupt the select-on-drop flow.
  */
  let imgSrc = ''
  if (isWeblink(preview)) {
    imgSrc = src || ''
  } else if (src) {
    // use the large image once loaded and when the image is being viewed > default width
    // const relativeScale = scale / previewDefaultScale(preview)
    // const viewScale = relativeScale * zoom
    // const progressiveImage = largeImageLoaded && viewScale > 1 ? src : src + '-small'
    // use the large image once loaded
    const progressiveImage = largeImageLoaded ? src : src + '-small'
    // console.log(progressiveImage, relativeScale, viewScale)
    imgSrc = `${progressiveImage}?${updatedAt.toMillis()}`
  } else if (!isPhotoShop) {
    imgSrc = previewSrc || ''
  }
  const [image] = useImage(imgSrc)

  if (src && previewSrc && image) {
    URL.revokeObjectURL(previewSrc)
  }

  const transformMedia = useCallback(
    createTransformMedia({
      instance,
      preview,
      firestore,
      canvasId,
      mediaRef: imgRef,
      setIsTransforming,
    }),
    [firestore, preview, canvasId],
  )

  const [linked] = useImage(iconLinked)
  const [unlinked] = useImage(iconUnlinked)
  const [linkedReadOnly] = useImage(iconLinkedReadOnly)
  const [unlinkedReadOnly] = useImage(iconUnlinkedReadOnly)

  let finalIconDefault
  let finalIconHover
  let finalIconSelected
  if (isWeblink(preview)) {
    finalIconDefault = preview.favIcon || iconWeblinkDefault
    finalIconHover = preview.favIcon || iconWeblinkHover
    finalIconSelected = preview.favIcon || iconWeblinkSelected
  } else {
    finalIconDefault = isPhotoShop ? iconPSD : iconImageDefault
    finalIconHover = isPhotoShop ? iconPSD : iconImageHover
    finalIconSelected = isPhotoShop ? iconPSD : iconImageSelected
  }
  const [iconDefault] = useImage(finalIconDefault)
  const [iconHover] = useImage(finalIconHover)
  const [iconSelected] = useImage(finalIconSelected)
  const [openWeblink] = useImage(iconOpenWeblink)

  const textWidth = (width * scale * zoom) / zoom - 2 * iconOuterWidth

  const roundCorners = (ctx: KonvaElement) => {
    const x = 0
    const y = 0
    const scaledWidth = width * scale
    const scaledHeight = height * scale
    const radius = 2 / zoom

    ctx.beginPath()
    ctx.moveTo(x + radius, y)
    ctx.lineTo(x + scaledWidth - radius, y)
    ctx.quadraticCurveTo(x + scaledWidth, y, x + scaledWidth, y + radius)
    ctx.lineTo(x + scaledWidth, y + scaledHeight - radius)
    ctx.quadraticCurveTo(x + scaledWidth, y + scaledHeight, x + scaledWidth - radius, y + scaledHeight)
    ctx.lineTo(x + radius, y + scaledHeight)
    ctx.quadraticCurveTo(x, y + scaledHeight, x, y + height - radius)
    ctx.lineTo(x, y + radius)
    ctx.quadraticCurveTo(x, y, x + radius, y)
    ctx.closePath()
  }

  // @TODO Reinstate fades when we get rid of lag
  //
  // const initializedRef = useRef<boolean>(false)
  // const nameRef = useRef<KonvaElement>(null)
  // const borderRef = useRef<KonvaElement>(null)
  // const linkRectRef = useRef<KonvaElement>(null)
  //
  // useEffect(() => {
  //   if (!selected) {
  //     if (isHovered) {
  //       fadeFill(nameRef.current, darkGray2)
  //       fadeInOpacity(borderRef.current)
  //     } else {
  //       if (initializedRef.current) {
  //         fadeFill(nameRef.current, darkGray1)
  //         fadeOutOpacity(borderRef.current)
  //       }
  //       initializedRef.current = true
  //     }
  //   }
  // }, [isHovered])
  //
  // useEffect(() => {
  //   if (isLinkHovered && linkActive) {
  //     fadeInOpacity(linkRectRef.current)
  //   } else {
  //     if (initializedRef.current) {
  //       fadeOutOpacity(linkRectRef.current)
  //     }
  //     initializedRef.current = true
  //   }
  // }, [isLinkHovered])

  useEffect(() => {
    if (instance && mediaAddToSelected && newMediaIdsRef?.current?.[instance.instanceId]) {
      if (selected) {
        delete newMediaIdsRef.current[instance.instanceId]
      } else {
        mediaAddToSelected(
          { evt: { shiftKey: true } } as KonvaMouseEvt,
          [preview, instance, draggableRef.current],
          false,
        )
      }
    }
  }, [instance, mediaAddToSelected, preview, newMediaIdsRef, selected])

  let linkStatusImage = linked
  let linkActive = false
  if (isWeblink(preview)) {
    linkStatusImage = openWeblink
    linkActive = true
  } else if (isLinked) {
    if (readOnly) {
      linkStatusImage = linkedReadOnly
    } else {
      linkStatusImage = linked
      linkActive = true
    }
  } else {
    if (readOnly) {
      linkStatusImage = unlinkedReadOnly
    } else {
      linkStatusImage = unlinked
    }
  }

  return (
    <Draggable
      draggable={isHoldingSpacebarRef !== undefined && !isHoldingSpacebarRef.current}
      x={X}
      y={Y}
      instance={instance}
      preview={preview}
      canvasId={canvasId}
      draggableRef={draggableRef}
      dragEndObjectsRef={dragEndObjectsRef}
      selectedObjectsRef={selectedObjectsRef}
    >
      <Group clipFunc={!inContainer && selected ? null : roundCorners}>
        <Image
          width={width}
          height={height}
          scale={{
            x: scale,
            y: scale,
          }}
          image={image}
          ref={imgRef}
          onTransformStart={() => {
            setIsTransforming(true)
          }}
          onTransformEnd={transformMedia}
          fill={lightGray1}
        />
      </Group>
      <Rect
        x={0}
        y={0}
        width={width * scale}
        height={height * scale}
        cornerRadius={2 / zoom}
        strokeWidth={1}
        strokeScaleEnabled={false}
        strokeEnabled={inContainer && selected ? true : isHovered && !selected ? true : false}
        stroke={inContainer && selected ? darkGray3 : darkGray1}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onMouseDown={async (e: KonvaMouseEvt) => {
          if (preview && instance && mediaAddToSelected && (!selected || e.evt.shiftKey) && !inContainer) {
            mediaAddToSelected(e, [preview, instance, draggableRef.current])
          }
          if (onMouseDown) {
            onMouseDown(e)
          }
          if (!inContainer) return

          e.cancelBubble = true
          if (initContainerPreviewsDrag) {
            initContainerPreviewsDrag(e, preview)
          }
        }}
        ondblclick={(e: KonvaMouseEvt) => {
          if (isWeblink(preview)) {
            mixpanel.track('Weblink Image Open')
            window.open(preview.url, '_blank')
          } else if (linkActive) {
            onOpenObject?.(e, preview, true)
          }
        }}
        onClick={e => {
          // we need to cancel propagation so stage is able to intentionally deselect everything
          // for clicks on blank stage
          e.cancelBubble = true
        }}
        onContextMenu={(e: KonvaMouseEvt) => {
          onRightClick?.(e, preview)
        }}
      />
      <Image
        image={selected ? iconSelected : isHovered ? iconHover : iconDefault}
        x={0}
        y={height * scale + paddingTop}
        width={textWidth < minTextWidth || isTransforming || iconWidth == null ? 0 : iconWidth}
        height={textWidth < minTextWidth || isTransforming || iconWidth == null ? 0 : iconWidth}
      />
      <Text
        x={iconWidth + iconPadding}
        y={height * scale + paddingTop}
        fill={selected ? darkGray3 : isHovered ? darkGray2 : darkGray1}
        text={name.replace(/\r?\n|\r/g, ' ')}
        lineHeight={1.25}
        width={textWidth < minTextWidth || isTransforming || iconWidth == null ? 0 : textWidth}
        fontSize={fontSize}
        fontFamily={'Lab Grotesque Light'}
        ellipsis={true}
        wrap={'none'}
      />
      <Group
        x={width * scale - iconOuterWidth}
        y={height * scale + paddingTop - iconPadding}
        onClick={(e: KonvaMouseEvt) => {
          if (isWeblink(preview)) {
            mixpanel.track('Weblink Url Open')
            window.open(preview.weblinkUrl, '_blank')
          } else if (linkActive) {
            onOpenObject?.(e, preview, false)
          }
        }}
        onMouseEnter={(e: any) => {
          if (linkActive) {
            e.target.getStage().container().style.cursor = 'pointer'
            setIsLinkHovered(true)
          }
        }}
        onMouseLeave={(e: any) => {
          if (linkActive) {
            e.target.getStage().container().style.cursor = 'default'
            setIsLinkHovered(false)
          }
        }}
      >
        <Rect
          fill={lightGray1}
          fillEnabled={isLinkHovered ? true : false}
          width={textWidth < minTextWidth || isTransforming || iconWidth == null ? 0 : iconOuterWidth}
          height={textWidth < minTextWidth || isTransforming || iconWidth == null ? 0 : iconOuterWidth}
          cornerRadius={2 / zoom}
        />
        <Image
          image={linkStatusImage}
          x={iconPadding}
          y={iconPadding}
          width={textWidth < minTextWidth || isTransforming || iconWidth == null ? 0 : iconWidth}
          height={textWidth < minTextWidth || isTransforming || iconWidth == null ? 0 : iconWidth}
        />
      </Group>
      {instance && imgRef.current && (
        <Transformer
          nodes={[imgRef.current]}
          showTransformer={selected && !inContainer && !isMultiSelect}
          enableAnchors={!isMultiSelect}
        />
      )}
    </Draggable>
  )
}
